import { formatPhoneNumberForDisplay, PhoneNumberFormat } from "@remi/formatting/phone";
import { RemiButton, type RemiButtonProps } from "@remi/next-ui";

import { useCopyToClipboard } from "~/hooks/useCopyToClipboard";

type CopyButtonProps = RemiButtonProps & {
  value: string;
  html?: string;
  valueType?: "phone" | "email";
};

const formatValueForCopyLabel = (value: string, valueType: CopyButtonProps["valueType"]): string =>
  valueType === "phone" ? formatPhoneNumberForDisplay(value, { format: PhoneNumberFormat.NATIONAL }) : value;

export const CopyButton = ({
  value,
  html,
  valueType,
  "aria-label": ariaLabel,
  children,
  ...props
}: CopyButtonProps) => {
  const { copy, copyHtml } = useCopyToClipboard();

  const onClick = async () => {
    if (html) {
      await copyHtml(html, value);
      return;
    }
    await copy(value);
  };

  // A visible label already gives the button an accessible name; a synthesized one here would override it.
  const defaultAriaLabel = valueType && !children ? `Copy ${formatValueForCopyLabel(value, valueType)}` : undefined;

  return (
    <RemiButton onClick={onClick} aria-label={ariaLabel ?? defaultAriaLabel} {...props}>
      {children}
    </RemiButton>
  );
};
